import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useAutoSave } from "@/hooks/use-auto-save";
import type { Todo } from "@/types/todo";
import { ChevronDown, X } from "lucide-react";
import { useState } from "react";
import { LabelSelector } from "./LabelSelector";

type TodoDetailFormProps = {
  todo: Todo;
  onSave: (changes: Record<string, unknown>) => void;
};

const PRIORITY_OPTIONS = [
  { value: "1", label: "急ぎ" },
  { value: "2", label: "普通" },
  { value: "3", label: "いつか" },
] as const;

/** Todo 詳細編集フォーム（自動保存付き） */
export const TodoDetailForm = ({ todo, onSave }: TodoDetailFormProps) => {
  const [note, setNote] = useState(todo.note ?? "");
  const [quantity, setQuantity] = useState(todo.quantity ?? "");
  const [suggestedVenue, setSuggestedVenue] = useState(
    todo.suggestedVenue ?? "",
  );
  const [dueDate, setDueDate] = useState(todo.dueDate ?? "");
  const [priority, setPriority] = useState(String(todo.priority ?? 2));
  const [url, setUrl] = useState(todo.url ?? "");
  const [isRecurring, setIsRecurring] = useState(todo.isRecurring);

  const { trackChange } = useAutoSave({ onSave });

  const handlePriorityChange = (value: string[]) => {
    if (value.length === 0) return;
    const selected = value[0];
    setPriority(selected);
    trackChange({ priority: Number(selected) });
  };

  const handleNoteChange = (value: string) => {
    setNote(value);
    trackChange({ note: value || null });
  };

  const handleQuantityChange = (value: string) => {
    setQuantity(value);
    trackChange({ quantity: value || null });
  };

  const handleVenueChange = (value: string) => {
    setSuggestedVenue(value);
    trackChange({ suggestedVenue: value || null });
  };

  const handleDueDateChange = (value: string) => {
    setDueDate(value);
    trackChange({ dueDate: value || null });
  };

  const handleUrlChange = (value: string) => {
    setUrl(value);
    trackChange({ url: value || null });
  };

  const handleRecurringChange = (checked: boolean) => {
    setIsRecurring(checked);
    trackChange({ isRecurring: checked });
  };

  return (
    <div className="flex flex-col gap-4">
      <ToggleGroup
        value={[priority]}
        onValueChange={handlePriorityChange}
        className="justify-start"
      >
        {PRIORITY_OPTIONS.map((opt) => (
          <ToggleGroupItem key={opt.value} value={opt.value}>
            {opt.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      <LabelSelector todoId={todo.id} />

      <Input
        placeholder="数量（例: 2本）"
        value={quantity}
        onChange={(e) => handleQuantityChange(e.target.value)}
      />

      <Input
        placeholder="購入先（例: スーパー）"
        value={suggestedVenue}
        onChange={(e) => handleVenueChange(e.target.value)}
      />

      {/* iOS Safari の日付ピッカーの「リセット」は制御 input では値が戻らないため、明示的なクリアボタンを置く */}
      <div className="flex items-center gap-2">
        <Input
          type="date"
          value={dueDate}
          onChange={(e) => handleDueDateChange(e.target.value)}
        />
        {dueDate && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="期限をクリア"
            onClick={() => handleDueDateChange("")}
          >
            <X className="size-4" />
          </Button>
        )}
      </div>

      <Textarea
        placeholder="メモ"
        value={note}
        onChange={(e) => handleNoteChange(e.target.value)}
        rows={3}
      />

      <Collapsible>
        <CollapsibleTrigger className="flex items-center gap-1 text-sm text-muted-foreground">
          <ChevronDown className="size-4" />
          その他
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-3 flex flex-col gap-3">
          <Input
            placeholder="通販URL"
            type="url"
            value={url}
            onChange={(e) => handleUrlChange(e.target.value)}
          />
          <div className="flex items-center justify-between text-sm">
            <span>繰り返し購入</span>
            <Switch
              checked={isRecurring}
              onCheckedChange={handleRecurringChange}
              aria-label="繰り返し購入"
            />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};
